import { z } from "zod";
import { schemas } from "./message-structure-schemas";

/**
 * WebSocket event schemas for real-time communication
 * These wrap domain events for WebSocket transport
 */

// Base WebSocket event structure
export const WebSocketEvent = z.object({
  type: z.string(),
  channel: z.string(),
  id: z.string(),
  timestamp: z.number().int(),
});

// Context event - signals context availability
export const ContextEvent = z.object({
  type: z.literal("context"),
  channel: z.string(),
  workspaceId: z.uuid(),
  conversationId: z.uuid(),
  threadId: z.uuid(),
});

// Outreach event - carries outreach messages
export const OutreachEvent = WebSocketEvent.extend({
  type: z.literal("outreach"),
  message: z.unknown(), // ChatMessage from message-structure-schemas
});

// Thread event - carries thread message updates
export const ThreadEvent = WebSocketEvent.extend({
  type: z.literal("thread"),
  messages: z.array(z.unknown()), // ChatMessage[] from message-structure-schemas
});

// Composer event - carries composition typing events
export const ComposerEvent = WebSocketEvent.extend({
  type: z.literal("composing"),
  event: schemas.ComposingEvent,
});

// Typing event - lightweight typing indicator (thread-level)
export const TypingEvent = WebSocketEvent.extend({
  type: z.literal("typing"),
  event: schemas.TypingEvent,
});

// Authorization event - for auth status updates
export const AuthorizerEvent = z.object({
  type: z.literal("authorization"),
  data: z.object({
    message: z.string(),
  }),
});

// Edge control event — CF → on-prem edge commands via WebSocketPublisherDO.
// Published to edge-control.<workspaceId> channel.
export const EdgeControlEvent = z.object({
  type: z.literal("edge_control"),
  action: z.discriminatedUnion("action", [
    z.object({ action: z.literal("cancel"), sessionId: z.string().min(1) }),
    z.object({
      action: z.literal("interrupt"),
      sessionId: z.string().min(1),
      message: z.string().min(1),
    }),
    z.object({
      action: z.literal("session_signal"),
      sessionId: z.string().min(1),
      signal: z.discriminatedUnion("type", [
        z.object({ type: z.literal("cancel") }),
        z.object({ type: z.literal("interrupt"), message: z.string().min(1) }),
      ]),
    }),
    z.object({
      action: z.literal("clear_signal"),
      sessionId: z.string().min(1),
    }),
    z.object({
      action: z.literal("register_tools"),
      sessionId: z.string().min(1),
      participantId: z.string().min(1),
      location: z.string().min(1),
      clientCapabilities: z.looseObject({}),
    }),
    z.object({
      action: z.literal("acp_response"),
      correlationId: z.string().min(1),
      // result is a base64-encoded AcpResponse proto binary.
      result: z.string().min(1),
    }),
    z.object({
      action: z.literal("heartbeat"),
      participantId: z.string().min(1),
    }),
  ]),
});

export const websocketEventSchemas = {
  WebSocketEvent,
  ContextEvent,
  OutreachEvent,
  ThreadEvent,
  ComposerEvent,
  TypingEvent,
  AuthorizerEvent,
  EdgeControlEvent,
};
